/**
 * Created by Ning Jiang on 3/27/2019.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {NudgeSpinReelController} from "@nolimitcity/slot-game/bin/game/reel/nudgereel/NudgeSpinReelController";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Elastic, TimelineLite} from "gsap";
import {PixiesVsPiratesGameConfig} from "../PixiesVsPiratesGameConfig";
import {PixiesVsPiratesParsedInitData} from "../server/parseddata/PixiesVsPiratesParsedInitData";
import {PPReelSymbolName} from "../symbols/PPReelSymbolName";
import {PPWildSymbol} from "../symbols/PPWildSymbol";

export class PPReelController extends NudgeSpinReelController {

    public static SYM_SIZE:{ [reelSize:number]:{ height:number, padding:number } } = {
        3 : {height : 134, padding : 6},
        4 : {height : 99, padding : 6},
        5 : {height : 78, padding : 6},
        6 : {height : 69, padding : 0}
    };

    private _reelSize:number;

    get reelSize():number {
        return this._reelSize;
    }

    set reelSize(value:number) {
        this.parseReelSize(value);
    }

    constructor(reelId:number) {
        super(reelId);
    }

    protected onInitDataParsed(data:PixiesVsPiratesParsedInitData):void {
        this.parseReelSize(data.gameAreaSize[this._reelId]);
        super.onInitDataParsed(data);
    }

    private parseReelSize(reelSize:number):void {
        const previousReelSize = this._reelSize;
        this._reelSize = reelSize;
        if(this._reelSize != previousReelSize) {
            this.initAnimator();
        }
    }

    protected initAnimator():void {
        this._reelPartAnimator.symWidth = GameConfig.instance.SYMBOL_WIDTH;
        this._reelPartAnimator.symBottomPadding = PPReelController.SYM_SIZE[this._reelSize].padding;
        this._reelPartAnimator.symHeight = PPReelController.SYM_SIZE[this._reelSize].height;
    }

    public isSpecialSymbol(symName:string):boolean {
        return super.isSpecialSymbol(symName) || symName === PPReelSymbolName.X;
    }

    protected addNewSymbol(symName:string, state?:ISymbolStateConfig):StateReelSymbol {
        if(symName === PPReelSymbolName.X) {
            const wilds:string[] = ArrayHelper.initArrayWithValues(this._reelSize, index => `W*${index}`);
            const part1:string[] = this._currentReelSet.slice(0, this._currentReelSetPosition);
            const part2:string[] = this._currentReelSet.slice(this._currentReelSetPosition + 1);
            this._currentReelSet = part1.concat(wilds).concat(part2);
            this._currentReelSetPosition += wilds.length - 1;
            symName = (this._currentReelSet[this._currentReelSetPosition]);
        }

        return super.addNewSymbol(symName, state);
    }

    // It will auto pick the config speed from the next new nudge.
    public updateNoWinNudgeSpeed():void {
        this._nudgeAnimator.nudgeStepSpeed = 0.3;
        this._nudgeAnimator.ease = Elastic.easeOut.config(1, 0.75);
    }

    public addExpandingSymbols(newSymName:string, mysterySymbolSize:number, positionId:number, reelSize:number):void {
        const symbolHeight:number = PPReelController.SYM_SIZE[reelSize].height;
        const symbolBottomPadding:number = PPReelController.SYM_SIZE[reelSize].padding;
        const symbolStep:number = symbolHeight + symbolBottomPadding;
        const mysterySymbolHeight:number = symbolHeight * mysterySymbolSize + symbolBottomPadding * (mysterySymbolSize - 1);
        const mysterySymbol:StateReelSymbol = this.findSymbol(positionId)!;
        const mysterySymbolIdInStopSymbols:number = positionId + this._symNumTop;

        // Remove the already used symbols so we only need to add symbols to the end of it.
        this._currentReelSet.splice(this._currentReelSetPosition, this._currentReelSet.length - this._currentReelSetPosition);
        this._currentReelSet = this._currentFullReelSet.concat(this._currentReelSet); // extend the reelset so make sure we don't finish the currentReelSet while removing Wilds.
        this._currentReelSetPosition = this._currentReelSet.length;

        // Replace symbol and add more symbols
        for(let i:number = 0; i < mysterySymbolSize; i++) {
            const newSymbol:StateReelSymbol = this.addNewSymbol(newSymName);
            newSymbol.symHeight = symbolHeight;
            newSymbol.bottomPadding = symbolBottomPadding;

            //TO DO discuss the approach
            newSymbol.changeState({state : PixiesVsPiratesGameConfig.instance.SYMBOL_STATES.normalExpand});
            // no need to update stack because it can never be W as revealed symbol.
            if(i === 0) {
                this.allSymbols.splice(mysterySymbolIdInStopSymbols, 1, newSymbol);
            } else {
                this.allSymbols.splice(mysterySymbolIdInStopSymbols + 1, 0, newSymbol);
                const removedSymbol:StateReelSymbol = this.allSymbols.shift()!;
                this._currentReelSet.push(removedSymbol.symName);
                this.removeReplacedSymbol(removedSymbol);
                this._currentReelSetPosition++;
            }
            newSymbol.position.set(0, mysterySymbol.y - mysterySymbolHeight / 2 + i * symbolStep + symbolHeight / 2);
        }

        // Fix stacked W symbol length
        if(mysterySymbolSize > 1) {
            const newStackLength:number = mysterySymbolSize + 2;
            (<number[]>GameConfig.instance.STACKED_SYMBOLS!["W"].totalNum)[this._reelId] = newStackLength;
            // if W above, need to change their name with index, and add more on top to connect to the currentReelSet
            for(let a:number = positionId - mysterySymbolSize; a > (5 - reelSize); a--) {
                const symbol:StateReelSymbol = this.findSymbol(a, false)!;
                if(symbol.symName.indexOf("W") > -1) {
                    this.fixStackedWildAbove(a + this._symNumTop, newStackLength, symbolHeight, symbolBottomPadding);
                    break;
                }
            }
            // if W under, need to add move in the nudge removed symbol pool. and update their size.
            for(let b:number = positionId + 1; b < 6; b++) {
                const symbol:StateReelSymbol = this.findSymbol(b, false)!;
                if(symbol.symName.indexOf("W") > -1) {
                    this.fixStackedWildBelow(b + this._symNumTop, newStackLength, symbolHeight, symbolBottomPadding);
                    break;
                }
            }
            (<number[]>GameConfig.instance.STACKED_SYMBOLS!["W"].totalNum)[this._reelId] = 3; // reset back
        }

        this._reelView.addChild(mysterySymbol); // move to top
        // No need to update targetPosition animation, the _reelPartAnimator.resetSymbolsPosition() will do it.
    }

    private fixStackedWildAbove(symIdInStopSymbols:number, newStackLength:number, symbolHeight:number, symbolBottomPadding:number):void {
        let currentStackedIndex:number = newStackLength - 1;
        const removeTimeline:TimelineLite = new TimelineLite();

        // Throw away all the W in the end of currentReelSet
        while(this._currentReelSet[this._currentReelSet.length - 1].indexOf("W") > -1) {
            this._currentReelSet.pop();
            this._currentReelSetPosition--;
        }
        // replace symbols in stopSymbols to W
        while(symIdInStopSymbols > -1 && currentStackedIndex > -1) {
            const oldSymbol:StateReelSymbol = this.allSymbols[symIdInStopSymbols]!;
            const newSymbol:PPWildSymbol = <PPWildSymbol>this.addNewSymbol(`W*${currentStackedIndex--}`);
            this.updateStackedSymbols(newSymbol, this.allSymbols[symIdInStopSymbols + 1], -1, true);
            newSymbol.symHeight = symbolHeight;
            newSymbol.bottomPadding = symbolBottomPadding;
            newSymbol.y = (symIdInStopSymbols - 10) * (symbolHeight + symbolBottomPadding) - symbolHeight / 2;
            this.allSymbols[symIdInStopSymbols] = newSymbol;
            removeTimeline.add(() => this.removeReplacedSymbol(oldSymbol), 0.01);
            symIdInStopSymbols--;
        }
        if(symIdInStopSymbols === -1) {
            // reel symbols are all replaced but more should be in currentReelSet
            const leftIndex:number = currentStackedIndex;
            while(currentStackedIndex > -1) {
                this._currentReelSet.push(`W*${leftIndex - currentStackedIndex--}`);
                this._currentReelSetPosition++;
            }
        } else {
            // If more W above in reel that connect to the expanded W, remove it and get the next symbol from reelset
            while(symIdInStopSymbols > -1) {
                if(this.allSymbols[symIdInStopSymbols]!.symName.indexOf("W") > -1) {
                    const oldSymbol:StateReelSymbol = this.allSymbols[symIdInStopSymbols]!;
                    this.allSymbols.splice(symIdInStopSymbols);
                    this.allSymbols.unshift(this.createNextSymbol(this.allSymbols[0]));
                    this.removeReplacedSymbol(oldSymbol);
                }
                const currentSymbol:StateReelSymbol = this.allSymbols[symIdInStopSymbols]!;
                currentSymbol.y = this.allSymbols[symIdInStopSymbols + 1]!.y - this.allSymbols[symIdInStopSymbols + 1]!.symHeight / 2 - currentSymbol.bottomPadding - currentSymbol.symHeight / 2;
                symIdInStopSymbols--;
            }
        }
    }

    private fixStackedWildBelow(symIdInStopSymbols:number, newStackLength:number, symbolHeight:number, symbolBottomPadding:number):void {
        let currentStackedIndex:number = 0;
        const removeTimeline:TimelineLite = new TimelineLite();
        while(symIdInStopSymbols < this.allSymbols.length && currentStackedIndex < newStackLength) {
            const oldSymbol:StateReelSymbol = this.allSymbols[symIdInStopSymbols]!;
            const newSymbol:PPWildSymbol = <PPWildSymbol>this.addNewSymbol(`W*${currentStackedIndex++}`);
            this.updateStackedSymbols(newSymbol, this.allSymbols[symIdInStopSymbols - 1], 1, true);
            newSymbol.symHeight = symbolHeight;
            newSymbol.bottomPadding = symbolBottomPadding;
            newSymbol.y = (symIdInStopSymbols - 10) * (symbolHeight + symbolBottomPadding) - symbolHeight / 2;
            this.allSymbols[symIdInStopSymbols] = newSymbol;
            removeTimeline.add(() => this.removeReplacedSymbol(oldSymbol), 0.01);
            symIdInStopSymbols++;
        }
        // Logger.logDev(`stopSymbols = ${this._stopSymbols.map(s => s!.symName).join()}`);
        // Logger.logDev(`pool symbols before = ${this._removedSymbolPool.map(s => s.symName).join()}`);

        // There is no case that the symIdInStopSymbols has anything left.

        if(currentStackedIndex < newStackLength) {
            // It will always add more W than removed. When meet index 0, it's a new stack.
            while(this._removedSymbolPool[0].stackedSymName!.symName === "W" && this._removedSymbolPool[0].stackedSymName!.index > 0) {
                this.removeReplacedSymbol(this._removedSymbolPool.shift()!);
            }

            const connectedWilds:StateReelSymbol[] = [];
            while(currentStackedIndex < newStackLength) {
                const newSymbol:PPWildSymbol = <PPWildSymbol>this.addNewSymbol(`W*${currentStackedIndex++}`);
                newSymbol.stack = this.allSymbols[this.allSymbols.length - 1]!.stack;
                this._reelView.removeChild(newSymbol);
                newSymbol.symHeight = symbolHeight;
                newSymbol.bottomPadding = symbolBottomPadding;
                newSymbol.y = 1.5 * symbolHeight + symbolBottomPadding * 2;
                connectedWilds.push(newSymbol);
            }
            this._removedSymbolPool = connectedWilds.concat(this._removedSymbolPool);
            while(this._removedSymbolPool.length > this._maxRemovedSymbolLength) {
                this._reelPartCreator.deleteSymbol(this._removedSymbolPool.pop()!);
            }
        }
        // TODO: do we need to care about the left symbol's y in pool?
        // Logger.logDev(`pool symbols after = ${this._removedSymbolPool.map(s => s.symName).join()}`);
        // No need to check if there is broken W left in the pool because the max nudge up is to show the full stacked W, no more down.
    }

    public removeReplacedSymbol(replacedSymbol:StateReelSymbol):void {
        // Can't use super.removeSymbol because we don't want to add them to the delete pool.
        this._reelView.removeChild(replacedSymbol);
        this._reelPartCreator.deleteSymbol(replacedSymbol);
    }

    public getRealSymId(symbol:StateReelSymbol):number {
        const stopSymbolIndex:number = this.allSymbols.indexOf(symbol);
        if(stopSymbolIndex === -1) {
            debugger;
            Logger.logDev(`PPReelController.getRealSymId(), symbol doesn't exist in reel!`);
        }
        return stopSymbolIndex - this._symNumTop;
    }

    public resetReelSymbols(reelSize:number, reelSymbols:string[]):void {
        this.parseReelSize(reelSize);

        // remove all current symbols.
        while(this.allSymbols.length > 0) {
            this.removeReplacedSymbol(this.allSymbols.pop()!);
        }
        // create new symbols
        this._stopReelSet = reelSymbols.concat();
        this.initReelStrip();
    }
}